/**
 * Owner → Live traffic.
 *
 * Answers one question: who is calling the API right now, and can I stop them
 * without phoning the hospital. Polls every 10s; the counters behind it are
 * exact rather than sampled, so the req/min column can be trusted.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldOff, Unlock } from 'lucide-react';
import { get, post } from '@/lib/api';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Card, CardContent, Field, Select, Spinner } from '@/components/ui/primitives';
import { visibleInterval } from '@/lib/polling';
import { OwnerConfirmDialog } from './OwnerConfirm';

interface TrafficRow {
  userId: string;
  name: string;
  email: string | null;
  roles: string[];
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  requestsPerMinute: number;
  windowRequests: number;
  rateLimitedCount: number;
  activeSessions: number;
  lastIp: string | null;
  lastUserAgent: string | null;
  lastPath: string | null;
  lastSeenAt: string | null;
  blockedForSeconds: number | null;
}

/**
 * A person working in a HIS generates single-digit requests per minute; a busy
 * front desk with polling worklists reaches the low tens. Sustained traffic
 * past these is a stuck tab or a script, not a human.
 */
const BUSY_RPM = 60;
const RUNAWAY_RPM = 120;

const BLOCK_OPTIONS = [
  { value: 0, label: 'Sign out only — they can sign back in immediately' },
  { value: 15, label: 'Sign out and block for 15 minutes' },
  { value: 60, label: 'Sign out and block for 1 hour' },
  { value: 240, label: 'Sign out and block for 4 hours' },
  { value: 1440, label: 'Sign out and block for 24 hours' },
];

function severity(row: TrafficRow): 'runaway' | 'busy' | 'normal' {
  if (row.requestsPerMinute >= RUNAWAY_RPM || row.rateLimitedCount > 0) return 'runaway';
  if (row.requestsPerMinute >= BUSY_RPM) return 'busy';
  return 'normal';
}

function formatBlockedFor(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes < 60) return `${minutes}m left`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m left`;
}

export function OwnerTrafficPage() {
  const queryClient = useQueryClient();
  const [windowMinutes, setWindowMinutes] = useState(5);
  const [pending, setPending] = useState<{ kind: 'force-logout' | 'unblock'; row: TrafficRow } | null>(
    null,
  );
  const [blockMinutes, setBlockMinutes] = useState(15);

  const traffic = useQuery({
    queryKey: ['platform-traffic', windowMinutes],
    queryFn: () => get<TrafficRow[]>(`/platform/traffic?windowMinutes=${windowMinutes}`),
    refetchInterval: visibleInterval(60_000),
  });

  const rows = traffic.data ?? [];
  const isUnblock = pending?.kind === 'unblock';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Live traffic"
        description="Who is calling the API right now, across every facility. Sign a runaway client out immediately."
      />

      <SearchFilterBar
        filters={
          <FilterField label="Window">
            <Select className="w-44" value={windowMinutes} onChange={(e) => setWindowMinutes(Number(e.target.value))}>
              <option value={1}>Last 1 minute</option>
              <option value={5}>Last 5 minutes</option>
              <option value={10}>Last 10 minutes</option>
            </Select>
          </FilterField>
        }
        trailing={
          <>
            <span className="text-xs text-muted-foreground">Refreshes every minute while this tab is open.</span>
            <Button variant="outline" size="sm" onClick={() => void traffic.refetch()} loading={traffic.isFetching}>
              Refresh
            </Button>
          </>
        }
        onReset={windowMinutes !== 5 ? () => setWindowMinutes(5) : undefined}
      />

      {traffic.isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            No authenticated traffic in the last {windowMinutes} minute
            {windowMinutes === 1 ? '' : 's'}.
          </CardContent>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          rowKey={(row) => row.userId}
          dense
          rowClassName={(row) => {
            const level = severity(row);
            return level === 'runaway' ? 'bg-destructive/10 odd:bg-destructive/10' : level === 'busy' ? 'bg-warning/10 odd:bg-warning/10' : undefined;
          }}
          columns={[
            {
              header: 'User',
              sort: (row) => row.name,
              cell: (row) => (
                <>
                  <div>{row.name}</div>
                  <div className="text-xs font-normal text-muted-foreground">{row.email ?? row.userId}</div>
                </>
              ),
            },
            {
              header: 'Hospital',
              sort: (row) => row.tenantSlug ?? row.tenantName,
              cell: (row) =>
                row.tenantSlug ? (
                  <>
                    <div>{row.tenantSlug}</div>
                    <div className="text-xs text-muted-foreground">{row.tenantName}</div>
                  </>
                ) : (
                  <span className="text-muted-foreground">{row.tenantName ?? '—'}</span>
                ),
            },
            {
              header: 'Req/min',
              numeric: true,
              sort: (row) => row.requestsPerMinute,
              cell: (row) => {
                const level = severity(row);
                return (
                  <span className={level === 'runaway' ? 'font-semibold text-destructive' : level === 'busy' ? 'font-semibold text-warning' : undefined}>
                    {row.requestsPerMinute}
                  </span>
                );
              },
            },
            { header: 'In window', numeric: true, sort: (row) => row.windowRequests, cell: (row) => row.windowRequests },
            {
              header: '429s',
              numeric: true,
              sort: (row) => row.rateLimitedCount,
              cell: (row) => (
                <span className={row.rateLimitedCount > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground'}>{row.rateLimitedCount}</span>
              ),
            },
            { header: 'Sessions', numeric: true, sort: (row) => row.activeSessions, cell: (row) => row.activeSessions },
            {
              header: 'Last seen',
              hideBelow: 'md',
              sort: (row) => row.lastSeenAt,
              cell: (row) => (
                <span className="text-xs text-muted-foreground">
                  <div>{row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleString() : '—'}</div>
                  {row.lastPath ? <div>{row.lastPath}</div> : null}
                </span>
              ),
            },
            {
              header: 'From',
              hideBelow: 'lg',
              sort: (row) => row.lastIp,
              cell: (row) => (
                <span className="text-xs text-muted-foreground">
                  <div>{row.lastIp ?? '—'}</div>
                  {row.blockedForSeconds !== null ? (
                    <Badge variant="destructive" className="mt-1">
                      Blocked · {formatBlockedFor(row.blockedForSeconds)}
                    </Badge>
                  ) : null}
                </span>
              ),
            },
          ]}
          rowActions={(row) =>
            row.tenantId ? (
              row.blockedForSeconds !== null ? (
                <Button variant="outline" size="xs" onClick={() => setPending({ kind: 'unblock', row })}>
                  <Unlock className="h-3.5 w-3.5" /> Unblock
                </Button>
              ) : (
                <Button variant="outline" size="xs" onClick={() => setPending({ kind: 'force-logout', row })}>
                  <ShieldOff className="h-3.5 w-3.5" /> Sign out
                </Button>
              )
            ) : (
              <span className="text-xs text-muted-foreground">Platform</span>
            )
          }
        />
      )}

      {pending !== null && (
        <OwnerConfirmDialog
          open={pending !== null}
          onClose={() => setPending(null)}
          title={isUnblock ? 'Lift the block' : 'Sign this user out'}
          description={
            pending ? `${pending.row.name}${pending.row.tenantSlug ? ` — ${pending.row.tenantSlug}` : ''}` : ''
          }
          warning={
            isUnblock ? (
              <>They will be able to sign in again straight away. Their previous sessions stay signed out.</>
            ) : (
              <>
                Signing out takes effect immediately, not whenever their current sign-in would have
                expired.
                {blockMinutes === 0
                  ? ' Without a block, a stuck browser tab will simply sign itself back in and carry on.'
                  : ' While blocked they cannot sign in again — you can lift it at any time.'}
              </>
            )
          }
          confirmLabel={isUnblock ? 'Lift block' : blockMinutes > 0 ? 'Sign out and block' : 'Sign out'}
          destructive={!isUnblock}
          action={({ totpCode, reason }) => {
            if (!pending?.row.tenantId) return Promise.reject(new Error('No facility for this user'));
            const base = `/platform/tenants/${encodeURIComponent(pending.row.tenantId)}/users/${encodeURIComponent(pending.row.userId)}`;
            return isUnblock
              ? post(`${base}/unblock`, { totpCode, reason })
              : post(`${base}/force-logout`, { totpCode, reason, blockMinutes });
          }}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['platform-traffic'] });
            setPending(null);
          }}
          successMessage={isUnblock ? 'Block lifted.' : 'Signed out.'}
        >
          {!isUnblock ? (
            <Field label="What should happen">
              <ChoiceButtons
                layout="cards"
                value={String(blockMinutes)}
                onChange={(value) => setBlockMinutes(Number(value))}
                options={BLOCK_OPTIONS.map((option) => ({ value: String(option.value), label: option.label }))}
              />
            </Field>
          ) : null}
        </OwnerConfirmDialog>
      )}
    </div>
  );
}
